import React, { useEffect, useState } from 'react';
import {
  Table, Button, message, Modal, Tag, Space, Row, Col,
  Select, Input, Card, Tooltip, Popconfirm, Statistic,
  Badge, Typography, List, Form, InputNumber, Switch,
  DatePicker, Collapse, Descriptions
} from 'antd';
import {
  EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined,
  ReloadOutlined, DownloadOutlined, CheckCircleOutlined,
  WarningOutlined, CloseCircleOutlined, DashboardOutlined,
  TrophyOutlined, RiseOutlined, FileTextOutlined,
  PaperClipOutlined, FolderOpenOutlined, SaveOutlined,
  FileOutlined, FullscreenOutlined, FullscreenExitOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import apiClient, { handleApiError } from '../utils/axiosConfig';
import XLSX from 'xlsx-js-style';
import ShipmentLogisticsScoreFields, { getShipmentLogisticsScore } from '../components/ShipmentLogisticsScoreFields';

const { Option } = Select;
const { Search } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

// ─────────────────────────────────────────────────────────────
// 🎨  PALETTE
// ─────────────────────────────────────────────────────────────
const P = {
  titleBg:     '1A1A2E',
  groupBg:     '16213E',
  headerBg:    '0F3460',
  altRow:      'F8FAFB',
  white:       'FFFFFF',
  classA_bg:   'D9F7BE',  classA_fg:  '135200',
  classB_bg:   'FFF7E6',  classB_fg:  '873800',
  classC_bg:   'FFF1F0',  classC_fg:  '820014',
  riskLow_bg:  'F6FFED',  riskLow_fg:  '135200',
  riskMed_bg:  'FFFBE6',  riskMed_fg:  '873800',
  riskHigh_bg: 'FFF1F0',  riskHigh_fg: '820014',
  scoreBg:     'F9F0FF',  scoreFg:    '531DAB',
  border:      'BDC3C7',
  borderDark:  '7F8C8D',
  white_f:     'FFFFFF',
  dark_f:      '2C3E50',
  gray_f:      '7F8C8D',
};

// ─────────────────────────────────────────────────────────────
// 🔧  STYLE FACTORIES
// ─────────────────────────────────────────────────────────────
const mkBorder = (color = P.border) => ({
  top:    { style: 'thin',   color: { rgb: color } },
  bottom: { style: 'thin',   color: { rgb: color } },
  left:   { style: 'thin',   color: { rgb: color } },
  right:  { style: 'thin',   color: { rgb: color } },
});

const mkBorderThick = () => ({
  top:    { style: 'medium', color: { rgb: P.borderDark } },
  bottom: { style: 'medium', color: { rgb: P.borderDark } },
  left:   { style: 'medium', color: { rgb: P.borderDark } },
  right:  { style: 'medium', color: { rgb: P.borderDark } },
});

// ✅ Temel hücre stili — fill.patternType zorunlu
const cs = (bg = P.white, fg = P.dark_f, bold = false, sz = 10, extraAlign = {}) => ({
  font:      { name: 'Calibri', sz, bold, color: { rgb: fg } },
  fill:      { patternType: 'solid', fgColor: { rgb: bg } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true, ...extraAlign },
  border:    mkBorder(),
});

const titleStyle = {
  font:      { name: 'Calibri', sz: 16, bold: true, color: { rgb: P.white_f } },
  fill:      { patternType: 'solid', fgColor: { rgb: P.titleBg } },
  alignment: { horizontal: 'left', vertical: 'center' },
  border:    mkBorderThick(),
};

const infoStyle = {
  font:      { name: 'Calibri', sz: 10, color: { rgb: P.gray_f } },
  fill:      { patternType: 'solid', fgColor: { rgb: 'F0F3F4' } },
  alignment: { horizontal: 'left', vertical: 'center' },
  border:    mkBorder(),
};

const groupHeaderStyle = (bg = P.groupBg) => ({
  font:      { name: 'Calibri', sz: 11, bold: true, color: { rgb: P.white_f } },
  fill:      { patternType: 'solid', fgColor: { rgb: bg } },
  alignment: { horizontal: 'center', vertical: 'center' },
  border:    mkBorderThick(),
});

const colHeaderStyle = {
  font:      { name: 'Calibri', sz: 10, bold: true, color: { rgb: P.white_f } },
  fill:      { patternType: 'solid', fgColor: { rgb: P.headerBg } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border:    mkBorder(P.borderDark),
};

// ✅ Risk & class stilleri
const riskStyle = (risk) => {
  if (risk === 1) return cs(P.riskLow_bg,  P.riskLow_fg,  true);
  if (risk === 3) return cs(P.riskMed_bg,  P.riskMed_fg,  true);
  if (risk === 9) return cs(P.riskHigh_bg, P.riskHigh_fg, true);
  return cs();
};

const riskLevelStyle = (level) => {
  if (level === 'Low')    return cs(P.riskLow_bg,  P.riskLow_fg,  true);
  if (level === 'Medium') return cs(P.riskMed_bg,  P.riskMed_fg,  true);
  if (level === 'High')   return cs(P.riskHigh_bg, P.riskHigh_fg, true);
  return cs();
};

const classStyle = (cls) => {
  if (cls === 'A') return cs(P.classA_bg, P.classA_fg, true, 11);
  if (cls === 'B') return cs(P.classB_bg, P.classB_fg, true, 11);
  if (cls === 'C') return cs(P.classC_bg, P.classC_fg, true, 11);
  return cs();
};

// ─────────────────────────────────────────────────────────────
// 🏗️  WORKBOOK BUILDER
// ─────────────────────────────────────────────────────────────
const buildExcelWorkbook = (data, t) => {
  const wb = XLSX.utils.book_new();

  const groups = [
    {
      label: t('supplier_name') + ' / ' + t('period'),
      bg: '2C3E50',
      cols: [
        { header: t('supplier_name'), key: 'supplierName', width: 24 },
        { header: t('supplier_code'), key: 'supplierCode', width: 14 },
        { header: t('period'),        key: 'period',       width: 12 },
      ],
    },
    {
      label: t('product_revenue_usd'),
      bg: '1A5276',
      cols: [
        { header: t('product_revenue_usd'),     key: 'productRevenue',        width: 18 },
        { header: t('product_quantity'),         key: 'productQuantity',       width: 16 },
        { header: t('defect_product_quantity'),  key: 'defectProductQuantity', width: 18 },
      ],
    },
    {
      label: 'PPM & ' + t('return_cost'),
      bg: '1F618D',
      cols: [
        { header: 'PPM (Auto)',          key: 'ppm',           width: 12 },
        { header: t('target_ppm'),       key: 'targetPPM',     width: 12 },
        { header: t('return_cost'),      key: 'returnCost',    width: 14 },
        { header: t('return_cost_paid'), key: 'returnCostPaid',width: 14 },
      ],
    },
    {
      label: t('audit_performance') + ' & ' + t('product_safety'),
      bg: '117A65',
      cols: [
        { header: t('audit_performance') + ' (%)', key: 'auditPerformance', width: 16 },
        { header: t('product_safety'),              key: 'productSafety',    width: 14 },
        { header: t('e_spice_deviations'),          key: 'eSPICEDeviations', width: 16 },
      ],
    },
    {
      label: t('development_metrics'),
      bg: '1E8449',
      cols: [
        { header: t('developed_projects_count'),     key: 'developedProjectsCount',    width: 20 },
        { header: t('manufacturing_maturity_count'), key: 'manufacturingMaturityCount', width: 24 },
        { header: t('shipment_logistics_success') + ' (%)', key: 'targetKPIAchievement',   width: 18 },
        { header: t('capa_closure_days'),            key: 'capaClosureDays',            width: 16 },
      ],
    },
    {
      label: t('certifications'),
      bg: '6C3483',
      cols: [
        { header: 'IATF 16949',      key: 'hasIATF16949', width: 12 },
        { header: 'ISO 14001',       key: 'hasISO14001',  width: 12 },
        { header: t('new_supplier'), key: 'isNewSupplier',width: 14 },
      ],
    },
    {
      label: t('risk_assessment'),
      bg: '922B21',
      cols: [
        { header: t('product_quantity_risk'),       key: 'ra_productQuantityRisk',    width: 18 },
        { header: t('ppm_risk'),                    key: 'ra_ppmRisk',                width: 10 },
        { header: t('audit_risk'),                  key: 'ra_auditRisk',              width: 12 },
        { header: t('kpi_risk'),                    key: 'ra_kpiRisk',                width: 10 },
        { header: t('capa_risk'),                   key: 'ra_capaRisk',               width: 12 },
        { header: t('developed_projects_risk'),     key: 'ra_developedProjectsRisk',  width: 20 },
        { header: t('manufacturing_maturity_risk'), key: 'ra_manufacturingMaturityRisk', width: 24 },
        { header: t('total_risk_score'),            key: 'ra_totalRiskScore',         width: 14 },
        { header: t('total_risk_level'),            key: 'ra_totalRiskLevel',         width: 14 },
      ],
    },
    {
      label: t('total_score') + ' & ' + t('supplier_class'),
      bg: '6D28D9',
      cols: [
        { header: t('total_score'),     key: 'totalScore',    width: 12 },
        { header: t('supplier_class'),  key: 'supplierClass', width: 14 },
        { header: t('audit_frequency'), key: 'auditFrequency',width: 14 },
      ],
    },
  ];

  const allCols   = groups.flatMap(g => g.cols);
  const totalCols = allCols.length;

  const ROW_TITLE  = 0;
  const ROW_INFO   = 1;
  const ROW_SPACER = 2;
  const ROW_GROUP  = 3;
  const ROW_HEADER = 4;
  const ROW_DATA   = 5;

  const ws = {};

  // ✅ setCell helper
  const setCell = (r, c, value, style, numType = null, numFmt = null) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    ws[addr] = { v: value, s: style };
    if (numType) ws[addr].t = numType;
    if (numFmt)  ws[addr].z = numFmt;
  };

  // ── Satır 0: Başlık ──
  setCell(ROW_TITLE, 0,
    `Supplier Performance Report  —  ${moment().format('DD MMMM YYYY, HH:mm')}`,
    titleStyle
  );
  for (let c = 1; c < totalCols; c++) {
    setCell(ROW_TITLE, c, '', titleStyle);
  }

  // ── Satır 1: Bilgi ──
  setCell(ROW_INFO, 0,
    `${t('total_records')}: ${data.length}   |   Generated: ${moment().format('DD/MM/YYYY HH:mm')}   |   Daiichi Supplier Portal`,
    infoStyle
  );
  for (let c = 1; c < totalCols; c++) {
    setCell(ROW_INFO, c, '', infoStyle);
  }

  // ── Satır 2: Boşluk ──
  for (let c = 0; c < totalCols; c++) {
    setCell(ROW_SPACER, c, '', cs('F0F3F4'));
  }

  // ── Satır 3: Grup header ──
  let colOffset = 0;
  groups.forEach(group => {
    setCell(ROW_GROUP, colOffset, group.label, groupHeaderStyle(group.bg));
    for (let c = 1; c < group.cols.length; c++) {
      setCell(ROW_GROUP, colOffset + c, '', groupHeaderStyle(group.bg));
    }
    colOffset += group.cols.length;
  });

  // ── Satır 4: Kolon header ──
  allCols.forEach((col, c) => {
    setCell(ROW_HEADER, c, col.header, colHeaderStyle);
  });

  // ── Satır 5+: Veri ──
  data.forEach((row, idx) => {
    const r      = ROW_DATA + idx;
    const isAlt  = idx % 2 === 1;
    const baseBg = isAlt ? P.altRow : P.white;
    const base   = cs(baseBg);
    const baseL  = cs(baseBg, P.dark_f, false, 10, { horizontal: 'left' });
    const baseBold = cs(baseBg, P.dark_f, true);
    const ra = row.riskAssessment || {};

    const perfColor = (val) => {
      if (val >= 80) return cs(P.riskLow_bg,  P.riskLow_fg,  true);
      if (val >= 70) return cs(P.riskMed_bg,  P.riskMed_fg,  true);
      return             cs(P.riskHigh_bg, P.riskHigh_fg, true);
    };

    const cellMap = {
      // ── Supplier info
      supplierName:  { v: row.supplierName || '',  s: { ...baseBold, alignment: { horizontal: 'left', vertical: 'center' } } },
      supplierCode:  { v: row.supplierCode || '',  s: baseL },
      period:        { v: row.period || '',         s: baseBold },

      // ── Revenue / Quantity
      productRevenue:        { v: row.productRevenue        || 0, s: base,   t: 'n', z: '#,##0.00' },
      productQuantity:       { v: row.productQuantity       || 0, s: base,   t: 'n', z: '#,##0'    },
      defectProductQuantity: { v: row.defectProductQuantity || 0, s: base,   t: 'n', z: '#,##0'    },

      // ── PPM
      ppm: {
        v: row.ppm != null ? parseFloat(row.ppm.toFixed(2)) : 0,
        s: row.ppm > 100
          ? cs(P.riskHigh_bg, P.riskHigh_fg, true)
          : cs(P.riskLow_bg,  P.riskLow_fg,  true),
        t: 'n', z: '0.00',
      },
      targetPPM:  { v: row.targetPPM  || 0, s: base, t: 'n', z: '#,##0' },
      returnCost: { v: row.returnCost || 0, s: base, t: 'n', z: '#,##0.00' },
      returnCostPaid: {
        v: row.returnCostPaid ? t('yes') : t('no'),
        s: row.returnCostPaid
          ? cs(P.riskLow_bg, P.riskLow_fg, true)
          : cs(baseBg),
      },

      // ── Audit / Safety
      auditPerformance: {
        v: row.auditPerformance || 0,
        s: perfColor(row.auditPerformance),
        t: 'n', z: '0.0',   // ✅ Düzeltildi: '0"%"' → '0.0'
      },
      productSafety:    { v: row.productSafety    ?? '-', s: riskStyle(row.productSafety)    },
      eSPICEDeviations: { v: row.eSPICEDeviations ?? '-', s: riskStyle(row.eSPICEDeviations) },

      // ── Development
      developedProjectsCount:     { v: row.developedProjectsCount     || 0, s: base, t: 'n' },
      manufacturingMaturityCount: { v: row.manufacturingMaturityCount  || 0, s: base, t: 'n' },
      targetKPIAchievement: {
        v: row.targetKPIAchievement || 0,
        s: perfColor(row.targetKPIAchievement),
        t: 'n', z: '0.0',   // ✅ Düzeltildi
      },
      capaClosureDays: { v: row.capaClosureDays || 0, s: base, t: 'n' },

      // ── Certs
      hasIATF16949: {
        v: row.hasIATF16949 ? t('yes') : t('no'),
        s: row.hasIATF16949
          ? cs(P.riskLow_bg,  P.riskLow_fg,  true)
          : cs(P.riskHigh_bg, P.riskHigh_fg, true),
      },
      hasISO14001: {
        v: row.hasISO14001 ? t('yes') : t('no'),
        s: row.hasISO14001
          ? cs(P.riskLow_bg,  P.riskLow_fg,  true)
          : cs(P.riskHigh_bg, P.riskHigh_fg, true),
      },
      isNewSupplier: {
        v: row.isNewSupplier ? t('yes') : t('no'),
        s: row.isNewSupplier ? cs('E8F4FD', '1A5276', true) : base,
      },

      // ── Risk assessment
      ra_productQuantityRisk:      { v: ra.productQuantityRisk      ?? '-', s: riskStyle(ra.productQuantityRisk)      },
      ra_ppmRisk:                  { v: ra.ppmRisk                  ?? '-', s: riskStyle(ra.ppmRisk)                  },
      ra_auditRisk:                { v: ra.auditRisk                ?? '-', s: riskStyle(ra.auditRisk)                },
      ra_kpiRisk:                  { v: ra.kpiRisk                  ?? '-', s: riskStyle(ra.kpiRisk)                  },
      ra_capaRisk:                 { v: ra.capaRisk                 ?? '-', s: riskStyle(ra.capaRisk)                 },
      ra_developedProjectsRisk:    { v: ra.developedProjectsRisk    ?? '-', s: riskStyle(ra.developedProjectsRisk)    },
      ra_manufacturingMaturityRisk:{ v: ra.manufacturingMaturityRisk ?? '-', s: riskStyle(ra.manufacturingMaturityRisk)},
      ra_totalRiskScore: {
        v: ra.totalRiskScore ?? '-',
        s: base,
        // ✅ Sadece gerçekten sayıysa 'n' tipini ver
        ...(typeof ra.totalRiskScore === 'number' ? { t: 'n', z: '0' } : {}),
      },
      ra_totalRiskLevel: { v: ra.totalRiskLevel ?? '-', s: riskLevelStyle(ra.totalRiskLevel) },

      // ── Score & class
      totalScore: {
        v: row.totalScore != null ? parseFloat(row.totalScore.toFixed(2)) : 0,
        s: cs(P.scoreBg, P.scoreFg, true, 12),
        t: 'n', z: '0.00',
      },
      supplierClass:  { v: row.supplierClass  || '-', s: classStyle(row.supplierClass) },
      auditFrequency: {
        v: row.auditFrequency ? `${row.auditFrequency}x/${t('year')}` : '-',
        s: base,
      },
    };

    allCols.forEach((col, c) => {
      const cell = cellMap[col.key];
      if (!cell) { setCell(r, c, '', base); return; }
      setCell(r, c, cell.v, cell.s, cell.t || null, cell.z || null);
    });
  });

  // ── Merges ──
  const merges = [
    { s: { r: ROW_TITLE,  c: 0 }, e: { r: ROW_TITLE,  c: totalCols - 1 } },
    { s: { r: ROW_INFO,   c: 0 }, e: { r: ROW_INFO,   c: totalCols - 1 } },
    { s: { r: ROW_SPACER, c: 0 }, e: { r: ROW_SPACER, c: totalCols - 1 } },
  ];

  let gOff = 0;
  groups.forEach(group => {
    if (group.cols.length > 1) {
      merges.push({
        s: { r: ROW_GROUP, c: gOff },
        e: { r: ROW_GROUP, c: gOff + group.cols.length - 1 },
      });
    }
    gOff += group.cols.length;
  });

  ws['!merges'] = merges;
  ws['!cols']   = allCols.map(col => ({ wch: col.width }));
  ws['!rows']   = [
    { hpt: 40 },  // title
    { hpt: 18 },  // info
    { hpt: 6  },  // spacer
    { hpt: 32 },  // group header
    { hpt: 38 },  // col header
    ...data.map(() => ({ hpt: 22 })),
  ];

  // ✅ Güvenli ref — data boşsa crash olmaz
  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: Math.max(ROW_DATA + data.length - 1, ROW_HEADER), c: totalCols - 1 },
  });

  XLSX.utils.book_append_sheet(wb, ws, 'Performance Data');
  return wb;
};

// ─────────────────────────────────────────────
// 🧩 COMPONENT
// ─────────────────────────────────────────────

const SupplierPerformanceDataList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fullscreenModalVisible, setFullscreenModalVisible] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);

  useEffect(() => { fetchSuppliers(); }, []);
  useEffect(() => { fetchPerformanceData(); }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/api/Suppliers/suppliers-only');
      setSuppliers(response.data || []);
    } catch (error) {
      message.error(handleApiError(error, t('error_fetching_suppliers')));
    }
  };

  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/SupplierPerformance');
      const performanceData = response.data || [];

      if (!performanceData.length) {
        message.warning(t('no_performance_data_available'));
        setDataSource([]);
        setFilteredData([]);
        return;
      }

      const uniquePeriods = [...new Set(performanceData.map(p => p.period))].sort().reverse();
      setPeriods(uniquePeriods);

      const formattedData = performanceData.map((item, index) => ({
        key: index,
        performanceDataId: item.performanceDataId,
        supplierId: item.supplierId,
        supplierName: item.supplier?.supplierName || 'N/A',
        supplierCode: item.supplier?.supplierCode || 'N/A',
        period: item.period,
        productRevenue: item.productRevenue,
        productQuantity: item.productQuantity,
        defectProductQuantity: item.defectProductQuantity,
        ppm: item.ppm,
        targetPPM: item.targetPPM,
        returnCost: item.returnCost,
        returnCostPaid: item.returnCostPaid,
        auditPerformance: item.auditPerformance,
        productSafety: item.productSafety,
        eSPICEDeviations: item.eSPICEDeviations,
        developedProjectsCount: item.developedProjectsCount,
        manufacturingMaturityCount: item.manufacturingMaturityCount,
        targetKPIAchievement: item.targetKPIAchievement,
        capaClosureDays: item.capaClosureDays,
        isNewSupplier: item.isNewSupplier,
        hasIATF16949: item.hasIATF16949,
        hasISO14001: item.hasISO14001,
        totalScore: item.totalScore,
        supplierClass: item.supplierClass,
        auditFrequency: item.auditFrequency,
        riskAssessment: item.riskAssessment,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));

      setDataSource(formattedData);
      setFilteredData(formattedData);
    } catch (error) {
      message.error(handleApiError(error, t('error_loading_data')));
      setDataSource([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttachments = async (performanceDataId) => {
    setLoadingAttachments(true);
    try {
      const response = await apiClient.get(`/api/PerformanceAttachment/performance/${performanceDataId}`);
      setAttachments(response.data || []);
    } catch {
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  useEffect(() => {
    let filtered = dataSource;
    if (supplierFilter) filtered = filtered.filter(i => i.supplierId === supplierFilter);
    if (periodFilter)   filtered = filtered.filter(i => i.period === periodFilter);
    if (classFilter)    filtered = filtered.filter(i => i.supplierClass === classFilter);
    setFilteredData(filtered);
  }, [supplierFilter, periodFilter, classFilter, dataSource]);

  const handleSearch = (value) => {
    const filtered = dataSource.filter(item =>
      item.supplierName.toLowerCase().includes(value.toLowerCase()) ||
      item.supplierCode.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const handleDelete = async (performanceDataId) => {
    try {
      await apiClient.delete(`/api/SupplierPerformance/${performanceDataId}`);
      message.success(t('performance_data_deleted_successfully'));
      fetchPerformanceData();
    } catch (error) {
      message.error(handleApiError(error, t('error_deleting_performance_data')));
    }
  };

  const showDetailModal = async (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
    await fetchAttachments(record.performanceDataId);
  };

  const showEditModal = (record) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      ...record,
      period: moment(record.period, 'YYYY-MM'),
      // ✅ Sevkiyat Lojistik Başarısı: eski toplamdan (targetKPIAchievement) hangi
      // Deadline/Damage/Documents seçimlerinin yapıldığı geri çıkarılamaz (birden
      // fazla kombinasyon aynı toplamı verebilir) — bilinçli olarak boş bırakılıyor,
      // admin her düzenlemede üçünü de yeniden seçmeli.
      deadlineScore: undefined,
      damageScore: undefined,
      documentsScore: undefined,
    });
    setEditModalVisible(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        supplierId: selectedRecord.supplierId,
        period: values.period.format('YYYY-MM'),
        productRevenue: values.productRevenue,
        productQuantity: values.productQuantity,
        defectProductQuantity: values.defectProductQuantity,
        targetPPM: values.targetPPM,
        returnCost: values.returnCost || 0,
        returnCostPaid: values.returnCostPaid || false,
        auditPerformance: values.auditPerformance,
        productSafety: values.productSafety,
        eSPICEDeviations: values.eSPICEDeviations,
        developedProjectsCount: values.developedProjectsCount,
        manufacturingMaturityCount: values.manufacturingMaturityCount,
        // ✅ Sevkiyat Lojistik Başarısı: Deadline + Damage + Documents toplamı,
        // mevcut backend alanı targetKPIAchievement üzerinden gönderiliyor (bkz. SupplierPerformanceDataEntry.js).
        targetKPIAchievement: getShipmentLogisticsScore(values),
        capaClosureDays: values.capaClosureDays,
        isNewSupplier: values.isNewSupplier || false,
        hasIATF16949: values.hasIATF16949 || false,
        hasISO14001: values.hasISO14001 || false,
      };
      await apiClient.put(`/api/SupplierPerformance/${selectedRecord.performanceDataId}`, payload);
      message.success(t('performance_data_updated_successfully'));
      setEditModalVisible(false);
      fetchPerformanceData();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(handleApiError(error, t('error_updating_performance_data')));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await apiClient.get(
        `/api/PerformanceAttachment/download/${attachmentId}`,
        { responseType: 'blob' }
      );
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success(t('file_downloaded_successfully'));
    } catch {
      message.error(t('error_downloading_file'));
    }
  };

  const handleExport = (scope) => {
    try {
      const exportData = scope === 'filtered' ? filteredData : dataSource;
      if (!exportData.length) { message.warning(t('no_data')); return; }
      const wb = buildExcelWorkbook(exportData, t);
      const label = scope === 'filtered' ? 'Filtered' : 'All';
      XLSX.writeFile(wb, `SupplierPerformance_${label}_${moment().format('YYYY-MM-DD_HH-mm')}.xlsx`);
      message.success(t('export_successful'));
      setExportModalVisible(false);
    } catch (err) {
      console.error('Export error:', err);
      message.error(t('export_failed'));
    }
  };

  // ── UI Helpers ──
  const getRiskColor     = (r) => r === 1 ? 'success' : r === 3 ? 'warning' : r === 9 ? 'error' : 'default';
  const getRiskIcon      = (r) => r === 1 ? <CheckCircleOutlined /> : r === 3 ? <WarningOutlined /> : r === 9 ? <CloseCircleOutlined /> : null;
  const getClassColor    = (c) => c === 'A' ? 'success' : c === 'B' ? 'warning' : c === 'C' ? 'error' : 'default';
  const getRiskLevelColor= (l) => l === 'Low' ? 'success' : l === 'Medium' ? 'warning' : l === 'High' ? 'error' : 'default';
  const formatFileSize   = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const calculateStats = () => {
    const total    = filteredData.length;
    const classA   = filteredData.filter(d => d.supplierClass === 'A').length;
    const classB   = filteredData.filter(d => d.supplierClass === 'B').length;
    const classC   = filteredData.filter(d => d.supplierClass === 'C').length;
    const avgScore = filteredData.length > 0
      ? (filteredData.reduce((sum, d) => sum + (d.totalScore || 0), 0) / filteredData.length).toFixed(2)
      : 0;
    return { total, classA, classB, classC, avgScore };
  };

  const stats = calculateStats();

  const columns = [
    {
      title: t('supplier_name'), dataIndex: 'supplierName', key: 'supplierName',
      width: 180, fixed: 'left',
      sorter: (a, b) => a.supplierName.localeCompare(b.supplierName),
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.supplierCode}</div>
        </div>
      ),
    },
    { title: t('period'), dataIndex: 'period', key: 'period', width: 120, fixed: 'left', sorter: (a, b) => a.period.localeCompare(b.period), render: (text) => <Tag color="blue">{text}</Tag> },
    { title: t('product_revenue_usd'), dataIndex: 'productRevenue', key: 'productRevenue', width: 150, sorter: (a, b) => a.productRevenue - b.productRevenue, render: (v) => `$${v?.toLocaleString() || 0}` },
    { title: t('product_quantity'), dataIndex: 'productQuantity', key: 'productQuantity', width: 130, sorter: (a, b) => a.productQuantity - b.productQuantity, render: (v) => v?.toLocaleString() || 0 },
    { title: t('product_quantity_risk'), dataIndex: 'riskAssessment', key: 'productQuantityRisk', width: 120, render: (ra) => <Tag color={getRiskColor(ra?.productQuantityRisk)} icon={getRiskIcon(ra?.productQuantityRisk)}>{ra?.productQuantityRisk || 'N/A'}</Tag> },
    { title: t('defect_product_quantity'), dataIndex: 'defectProductQuantity', key: 'defectProductQuantity', width: 150, sorter: (a, b) => a.defectProductQuantity - b.defectProductQuantity },
    { title: 'PPM (Auto)', dataIndex: 'ppm', key: 'ppm', width: 120, sorter: (a, b) => (a.ppm || 0) - (b.ppm || 0), render: (ppm) => <Tag color={ppm > 100 ? 'error' : 'success'}>{ppm?.toFixed(2) || 'N/A'}</Tag> },
    { title: t('target_ppm'), dataIndex: 'targetPPM', key: 'targetPPM', width: 120, sorter: (a, b) => a.targetPPM - b.targetPPM },
    { title: t('ppm_risk'), dataIndex: 'riskAssessment', key: 'ppmRisk', width: 100, render: (ra) => <Tag color={getRiskColor(ra?.ppmRisk)} icon={getRiskIcon(ra?.ppmRisk)}>{ra?.ppmRisk || 'N/A'}</Tag> },
    { title: t('return_cost'), dataIndex: 'returnCost', key: 'returnCost', width: 130, sorter: (a, b) => a.returnCost - b.returnCost, render: (v) => `$${v?.toLocaleString() || 0}` },
    { title: t('return_cost_paid'), dataIndex: 'returnCostPaid', key: 'returnCostPaid', width: 120, render: (paid) => <Tag color={paid ? 'success' : 'default'}>{paid ? t('yes') : t('no')}</Tag> },
    { title: t('audit_performance'), dataIndex: 'auditPerformance', key: 'auditPerformance', width: 140, sorter: (a, b) => a.auditPerformance - b.auditPerformance, render: (v) => <Tag color={v >= 80 ? 'success' : v >= 70 ? 'warning' : 'error'}>{v}%</Tag> },
    { title: t('audit_risk'), dataIndex: 'riskAssessment', key: 'auditRisk', width: 100, render: (ra) => <Tag color={getRiskColor(ra?.auditRisk)} icon={getRiskIcon(ra?.auditRisk)}>{ra?.auditRisk || 'N/A'}</Tag> },
    { title: t('product_safety'), dataIndex: 'productSafety', key: 'productSafety', width: 120, render: (v) => <Tag color={getRiskColor(v)}>{v || 'N/A'}</Tag> },
    { title: t('e_spice_deviations'), dataIndex: 'eSPICEDeviations', key: 'eSPICEDeviations', width: 140, render: (v) => <Tag color={getRiskColor(v)}>{v || 'N/A'}</Tag> },
    { title: t('developed_projects_count'), dataIndex: 'developedProjectsCount', key: 'developedProjectsCount', width: 150, sorter: (a, b) => a.developedProjectsCount - b.developedProjectsCount },
    { title: t('developed_projects_risk'), dataIndex: 'riskAssessment', key: 'developedProjectsRisk', width: 150, render: (ra) => <Tag color={getRiskColor(ra?.developedProjectsRisk)} icon={getRiskIcon(ra?.developedProjectsRisk)}>{ra?.developedProjectsRisk || 'N/A'}</Tag> },
    { title: t('manufacturing_maturity_count'), dataIndex: 'manufacturingMaturityCount', key: 'manufacturingMaturityCount', width: 180, sorter: (a, b) => a.manufacturingMaturityCount - b.manufacturingMaturityCount },
    { title: t('manufacturing_maturity_risk'), dataIndex: 'riskAssessment', key: 'manufacturingMaturityRisk', width: 180, render: (ra) => <Tag color={getRiskColor(ra?.manufacturingMaturityRisk)} icon={getRiskIcon(ra?.manufacturingMaturityRisk)}>{ra?.manufacturingMaturityRisk || 'N/A'}</Tag> },
    { title: t('shipment_logistics_success'), dataIndex: 'targetKPIAchievement', key: 'targetKPIAchievement', width: 150, sorter: (a, b) => a.targetKPIAchievement - b.targetKPIAchievement, render: (v) => <Tag color={v >= 80 ? 'success' : v >= 70 ? 'warning' : 'error'}>{v}%</Tag> },
    { title: t('kpi_risk'), dataIndex: 'riskAssessment', key: 'kpiRisk', width: 100, render: (ra) => <Tag color={getRiskColor(ra?.kpiRisk)} icon={getRiskIcon(ra?.kpiRisk)}>{ra?.kpiRisk || 'N/A'}</Tag> },
    { title: t('capa_closure_days'), dataIndex: 'capaClosureDays', key: 'capaClosureDays', width: 140, sorter: (a, b) => a.capaClosureDays - b.capaClosureDays },
    { title: t('capa_risk'), dataIndex: 'riskAssessment', key: 'capaRisk', width: 100, render: (ra) => <Tag color={getRiskColor(ra?.capaRisk)} icon={getRiskIcon(ra?.capaRisk)}>{ra?.capaRisk || 'N/A'}</Tag> },
    { title: 'IATF 16949', dataIndex: 'hasIATF16949', key: 'hasIATF16949', width: 120, render: (v) => <Tag color={v ? 'success' : 'default'} icon={v ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>{v ? t('yes') : t('no')}</Tag> },
    { title: 'ISO 14001', dataIndex: 'hasISO14001', key: 'hasISO14001', width: 120, render: (v) => <Tag color={v ? 'success' : 'default'} icon={v ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>{v ? t('yes') : t('no')}</Tag> },
    { title: t('new_supplier'), dataIndex: 'isNewSupplier', key: 'isNewSupplier', width: 120, render: (v) => <Tag color={v ? 'blue' : 'default'}>{v ? t('yes') : t('no')}</Tag> },
    { title: t('total_score'), dataIndex: 'totalScore', key: 'totalScore', width: 120, sorter: (a, b) => a.totalScore - b.totalScore, render: (v) => <Tag color="processing" style={{ fontSize: '14px', fontWeight: 'bold' }}>{v?.toFixed(2) || 'N/A'}</Tag> },
    { title: t('supplier_class'), dataIndex: 'supplierClass', key: 'supplierClass', width: 120, sorter: (a, b) => (a.supplierClass || '').localeCompare(b.supplierClass || ''), render: (v) => <Tag color={getClassColor(v)} style={{ fontSize: '14px', fontWeight: 'bold' }}>{v || 'N/A'}</Tag> },
    { title: t('audit_frequency'), dataIndex: 'auditFrequency', key: 'auditFrequency', width: 140, render: (v) => <Tag color="cyan">{v ? `${v}x/${t('year')}` : 'N/A'}</Tag> },
    { title: t('total_risk_level'), dataIndex: 'riskAssessment', key: 'totalRiskLevel', width: 150, render: (ra) => ra ? <Tag color={getRiskLevelColor(ra.totalRiskLevel)}>{ra.totalRiskLevel} ({ra.totalRiskScore})</Tag> : <Tag>N/A</Tag> },
    {
      title: t('actions'), key: 'actions', width: 180, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title={t('view_details')}>
            <Button type="primary" icon={<EyeOutlined />} size="small" onClick={() => showDetailModal(record)} />
          </Tooltip>
          <Tooltip title={t('edit')}>
            <Button type="default" icon={<EditOutlined />} size="small" onClick={() => showEditModal(record)} />
          </Tooltip>
          <Popconfirm
            title={t('delete_performance_data_confirm')}
            description={`${record.supplierName} - ${record.period}`}
            onConfirm={() => handleDelete(record.performanceDataId)}
            okText={t('yes')} cancelText={t('no')} okButtonProps={{ danger: true }}
          >
            <Tooltip title={t('delete')}>
              <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tableProps = (extraScroll = {}) => ({
    dataSource: filteredData,
    columns,
    loading,
    scroll: { x: 4500, ...extraScroll },
    pagination: {
      pageSize: 10,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50', '100'],
      showTotal: (total) => `${t('total')} ${total} ${t('records')}`,
    },
  });

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{
        background: '#fff', padding: '32px', borderRadius: '12px',
        marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderLeft: '4px solid #1890ff',
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: '#262626' }}>
              <DashboardOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
              {t('supplier_performance_data_management')}
            </Title>
            <Text type="secondary" style={{ fontSize: '15px' }}>
              {t('view_and_manage_supplier_performance_records')}
            </Text>
          </Col>
          <Col>
            <Space size="middle">
              <Button type="default" icon={<FullscreenOutlined />} size="large"
                onClick={() => setFullscreenModalVisible(true)}
                style={{ borderColor: '#722ed1', color: '#722ed1', fontWeight: '600' }}>
                {t('fullscreen_view')}
              </Button>
              <Button type="primary" icon={<PlusOutlined />} size="large"
                onClick={() => navigate('/admin-dashboard/performance-data-entry')}
                style={{ fontWeight: '600' }}>
                {t('add_new_entry')}
              </Button>
              <Button icon={<ReloadOutlined />} size="large" onClick={fetchPerformanceData}>
                {t('refresh')}
              </Button>
              <Button icon={<DownloadOutlined />} size="large"
                onClick={() => setExportModalVisible(true)}
                style={{ borderColor: '#52c41a', color: '#52c41a', fontWeight: '600' }}>
                {t('export_excel')}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}><Card><Statistic title={t('total_records')} value={stats.total} prefix={<FileTextOutlined />} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col xs={24} sm={12} md={6}><Card><Statistic title={`${t('supplier_class')} A`} value={stats.classA} prefix={<TrophyOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={24} sm={12} md={6}><Card><Statistic title={`${t('supplier_class')} B`} value={stats.classB} prefix={<WarningOutlined />} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col xs={24} sm={12} md={6}><Card><Statistic title={t('average_score')} value={stats.avgScore} suffix="/ 100" prefix={<RiseOutlined />} valueStyle={{ color: '#722ed1' }} /></Card></Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Select placeholder={t('filter_by_supplier')} allowClear onChange={v => setSupplierFilter(v)} style={{ width: '100%' }} size="large" showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
              {suppliers.map(s => <Option key={s.supplierId} value={s.supplierId}>{s.supplierName}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select placeholder={t('filter_by_period')} allowClear onChange={v => setPeriodFilter(v)} style={{ width: '100%' }} size="large">
              {periods.map(p => <Option key={p} value={p}>{p}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select placeholder={t('filter_by_class')} allowClear onChange={v => setClassFilter(v)} style={{ width: '100%' }} size="large">
              <Option value="A"><Tag color="success">A - {t('excellent')}</Tag></Option>
              <Option value="B"><Tag color="warning">B - {t('good')}</Tag></Option>
              <Option value="C"><Tag color="error">C - {t('needs_improvement')}</Tag></Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search placeholder={t('search_by_supplier')} allowClear onSearch={handleSearch} style={{ width: '100%' }} size="large" />
          </Col>
        </Row>
      </Card>

      {/* Main Table */}
      <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Table {...tableProps()} />
      </Card>

      {/* Export Modal */}
      <Modal
        title={<Space><DownloadOutlined style={{ color: '#52c41a', fontSize: '20px' }} /><span style={{ fontSize: '18px', fontWeight: '600' }}>{t('export_excel')}</span></Space>}
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        footer={null}
        width={480}
        centered
      >
        <div style={{ padding: '8px 0 16px' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: '24px', fontSize: '14px' }}>
            {t('export_scope_description')}
          </Text>
          <Card hoverable onClick={() => handleExport('filtered')}
            style={{ marginBottom: '16px', borderRadius: '10px', border: '2px solid #1890ff', cursor: 'pointer' }}>
            <Space align="start">
              <FilterOutlined style={{ fontSize: '28px', color: '#1890ff', marginTop: '4px' }} />
              <div>
                <Text strong style={{ fontSize: '15px', display: 'block' }}>{t('export_filtered_data')}</Text>
                <Text type="secondary" style={{ fontSize: '13px' }}>{t('export_filtered_desc', { count: filteredData.length })}</Text>
              </div>
            </Space>
          </Card>
          <Card hoverable onClick={() => handleExport('all')}
            style={{ borderRadius: '10px', border: '2px solid #52c41a', cursor: 'pointer' }}>
            <Space align="start">
              <DownloadOutlined style={{ fontSize: '28px', color: '#52c41a', marginTop: '4px' }} />
              <div>
                <Text strong style={{ fontSize: '15px', display: 'block' }}>{t('export_all_data')}</Text>
                <Text type="secondary" style={{ fontSize: '13px' }}>{t('export_all_desc', { count: dataSource.length })}</Text>
              </div>
            </Space>
          </Card>
        </div>
      </Modal>

      {/* Fullscreen Modal */}
      <Modal
        title={<Space><FullscreenOutlined style={{ color: '#722ed1', fontSize: '20px' }} /><span style={{ fontSize: '18px', fontWeight: '600' }}>{t('fullscreen_table_view')}</span></Space>}
        open={fullscreenModalVisible}
        onCancel={() => setFullscreenModalVisible(false)}
        footer={[<Button key="close" type="primary" icon={<FullscreenExitOutlined />} onClick={() => setFullscreenModalVisible(false)} size="large">{t('close_fullscreen')}</Button>]}
        width="95vw" style={{ top: 20 }}
        bodyStyle={{ padding: '16px', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Table {...tableProps({ y: 'calc(100vh - 300px)' })} pagination={{ ...tableProps().pagination, pageSize: 20 }} size="small" />
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={<Space><EyeOutlined style={{ color: '#1890ff', fontSize: '20px' }} /><span style={{ fontSize: '18px', fontWeight: '600' }}>{t('performance_details')}</span></Space>}
        open={detailModalVisible}
        onCancel={() => { setDetailModalVisible(false); setAttachments([]); }}
        footer={[<Button key="close" type="primary" onClick={() => setDetailModalVisible(false)}>{t('close')}</Button>]}
        width={1000} style={{ top: 20 }}
      >
        {selectedRecord && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '8px' }}>
            <Card size="small" style={{ marginBottom: '16px', background: '#f0f5ff' }}
              title={<Space><TrophyOutlined style={{ color: '#1890ff' }} /><span>{selectedRecord.supplierName}</span></Space>}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label={t('supplier_code')}>{selectedRecord.supplierCode}</Descriptions.Item>
                <Descriptions.Item label={t('period')}><Tag color="blue">{selectedRecord.period}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('total_score')}><Tag color="processing" style={{ fontSize: '16px', fontWeight: 'bold' }}>{selectedRecord.totalScore?.toFixed(2)} / 100</Tag></Descriptions.Item>
                <Descriptions.Item label={t('supplier_class')}><Tag color={getClassColor(selectedRecord.supplierClass)} style={{ fontSize: '16px', fontWeight: 'bold' }}>{selectedRecord.supplierClass}</Tag></Descriptions.Item>
              </Descriptions>
            </Card>
            <Card size="small" title={t('quality_metrics')} style={{ marginBottom: '16px' }}>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label={t('product_revenue_usd')}>${selectedRecord.productRevenue?.toLocaleString()}</Descriptions.Item>
                <Descriptions.Item label={t('product_quantity')}>{selectedRecord.productQuantity?.toLocaleString()}</Descriptions.Item>
                <Descriptions.Item label={t('defect_product_quantity')}>{selectedRecord.defectProductQuantity}</Descriptions.Item>
                <Descriptions.Item label="PPM"><Tag color={selectedRecord.ppm > 100 ? 'error' : 'success'}>{selectedRecord.ppm?.toFixed(2)}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('target_ppm')}>{selectedRecord.targetPPM}</Descriptions.Item>
                <Descriptions.Item label={t('return_cost')}>${selectedRecord.returnCost?.toLocaleString()}</Descriptions.Item>
                <Descriptions.Item label={t('return_cost_paid')}><Tag color={selectedRecord.returnCostPaid ? 'success' : 'default'}>{selectedRecord.returnCostPaid ? t('yes') : t('no')}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('audit_performance')}><Tag color={selectedRecord.auditPerformance >= 80 ? 'success' : 'warning'}>{selectedRecord.auditPerformance}%</Tag></Descriptions.Item>
              </Descriptions>
            </Card>
            <Card size="small" title={t('development_metrics')} style={{ marginBottom: '16px' }}>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label={t('product_safety')}><Tag color={getRiskColor(selectedRecord.productSafety)}>{selectedRecord.productSafety}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('e_spice_deviations')}><Tag color={getRiskColor(selectedRecord.eSPICEDeviations)}>{selectedRecord.eSPICEDeviations}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('developed_projects_count')}>{selectedRecord.developedProjectsCount}</Descriptions.Item>
                <Descriptions.Item label={t('manufacturing_maturity_count')}>{selectedRecord.manufacturingMaturityCount}</Descriptions.Item>
                <Descriptions.Item label={t('shipment_logistics_success')}><Tag color={selectedRecord.targetKPIAchievement >= 80 ? 'success' : 'warning'}>{selectedRecord.targetKPIAchievement}%</Tag></Descriptions.Item>
                <Descriptions.Item label={t('capa_closure_days')}>{selectedRecord.capaClosureDays}</Descriptions.Item>
              </Descriptions>
            </Card>
            {selectedRecord.riskAssessment && (
              <Card size="small" title={t('risk_assessment')} style={{ marginBottom: '16px' }}>
                <Row gutter={[8, 8]}>
                  {[
                    { label: t('product_quantity_risk'), key: 'productQuantityRisk' },
                    { label: t('ppm_risk'),              key: 'ppmRisk'             },
                    { label: t('audit_risk'),            key: 'auditRisk'           },
                  ].map(({ label, key }) => (
                    <Col span={8} key={key}>
                      <Card size="small" style={{ textAlign: 'center', background: '#fafafa' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>{label}</Text>
                        <div style={{ marginTop: '8px' }}>
                          <Tag
                            color={getRiskColor(selectedRecord.riskAssessment[key])}
                            icon={getRiskIcon(selectedRecord.riskAssessment[key])}
                          >
                            {selectedRecord.riskAssessment[key]}
                          </Tag>
                        </div>
                      </Card>
                    </Col>
                  ))}
                  <Col span={24}>
                    <Card size="small" style={{ textAlign: 'center', background: '#fff1f0' }}>
                      <Text strong>{t('total_risk_level')}</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Tag
                          color={getRiskLevelColor(selectedRecord.riskAssessment.totalRiskLevel)}
                          style={{ fontSize: '16px', padding: '4px 12px' }}
                        >
                          {selectedRecord.riskAssessment.totalRiskLevel} ({selectedRecord.riskAssessment.totalRiskScore})
                        </Tag>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Card>
            )}
            <Card size="small" title={t('certifications')} style={{ marginBottom: '16px' }}>
              <Space size="large">
                {[
                  { label: 'IATF 16949',      val: selectedRecord.hasIATF16949  },
                  { label: 'ISO 14001',        val: selectedRecord.hasISO14001   },
                  { label: t('new_supplier'),  val: selectedRecord.isNewSupplier },
                ].map(({ label, val }) => (
                  <div key={label}>
                    <Text strong>{label}: </Text>
                    <Tag
                      color={val ? 'success' : 'default'}
                      icon={val ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    >
                      {val ? t('yes') : t('no')}
                    </Tag>
                  </div>
                ))}
              </Space>
            </Card>
            <Card
              size="small"
              title={
                <Space>
                  <PaperClipOutlined style={{ color: '#1890ff' }} />
                  <span>{t('attachments')}</span>
                  <Badge count={attachments.length} showZero style={{ backgroundColor: '#52c41a' }} />
                </Space>
              }
            >
              {loadingAttachments ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <ReloadOutlined spin style={{ fontSize: '32px', color: '#1890ff' }} />
                  <div style={{ marginTop: '16px', color: '#8c8c8c' }}>{t('loading_attachments')}</div>
                </div>
              ) : attachments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', background: '#fafafa', borderRadius: '8px' }}>
                  <FolderOpenOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                  <div style={{ marginTop: '12px', color: '#8c8c8c', fontSize: '14px' }}>{t('no_attachments_found')}</div>
                </div>
              ) : (
                <List
                  dataSource={attachments}
                  renderItem={(file) => (
                    <List.Item
                      style={{ padding: '12px', background: '#fafafa', marginBottom: '8px', borderRadius: '8px' }}
                      actions={[
                        <Button
                          type="primary"
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownloadAttachment(file.attachmentId, file.fileName)}
                        >
                          {t('download')}
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<FileOutlined style={{ fontSize: '28px', color: '#1890ff' }} />}
                        title={<Text strong>{file.fileName}</Text>}
                        description={
                          <Space direction="vertical" size={2}>
                            <Space size="small">
                              <Tag color="blue">{file.fieldName}</Tag>
                              <Tag color="green">{formatFileSize(file.fileSize)}</Tag>
                            </Space>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {moment(file.uploadedAt).format('DD/MM/YYYY HH:mm')}
                            </Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>{t('edit_performance_data')}</span>
          </Space>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>{t('cancel')}</Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleEditSave}>{t('save')}</Button>,
        ]}
        width={900}
      >
        <Form form={form} layout="vertical">
          <Collapse defaultActiveKey={['1', '2', '3', '4']}>
            <Panel header={t('basic_information')} key="1">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="period" label={t('period')} rules={[{ required: true }]}>
                    <DatePicker.MonthPicker format="YYYY-MM" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="productRevenue" label={t('product_revenue_usd')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>

            <Panel header={t('quality_metrics')} key="2">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="productQuantity" label={t('product_quantity')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="defectProductQuantity" label={t('defect_product_quantity')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="targetPPM" label={t('target_ppm')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="returnCost" label={t('return_cost')}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="returnCostPaid" label={t('return_cost_paid')} valuePropName="checked">
                    <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="auditPerformance" label={t('audit_performance')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="%" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>

            <Panel header={t('development_metrics')} key="3">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="productSafety" label={t('product_safety')} rules={[{ required: true }]}>
                    <Select>
                      <Option value={1}><Tag color="success">1 - {t('low_risk')}</Tag></Option>
                      <Option value={9}><Tag color="error">9 - {t('high_risk')}</Tag></Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="eSPICEDeviations" label={t('e_spice_deviations')} rules={[{ required: true }]}>
                    <Select>
                      <Option value={1}><Tag color="success">1 - {t('low_risk')}</Tag></Option>
                      <Option value={9}><Tag color="error">9 - {t('high_risk')}</Tag></Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="developedProjectsCount" label={t('developed_projects_count')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="manufacturingMaturityCount" label={t('manufacturing_maturity_count')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} max={65} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="capaClosureDays" label={t('capa_closure_days')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>

              {/* ✅ Sevkiyat Lojistik Başarısı - eski targetKPIAchievement manuel girişinin yerine (paylaşılan bileşen) */}
              <ShipmentLogisticsScoreFields form={form} note={t('shipment_logistics_reselect_note')} />
            </Panel>

            <Panel header={t('certifications')} key="4">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="isNewSupplier" label={t('new_supplier')} valuePropName="checked">
                    <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="hasIATF16949" label="IATF 16949" valuePropName="checked">
                    <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="hasISO14001" label="ISO 14001" valuePropName="checked">
                    <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>
        </Form>
      </Modal>

    </div>
  );
};

export default SupplierPerformanceDataList;

